import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { SetupStatusChip } from 'src/interfaces/oatfiBusiness'

interface Props {
  chips: SetupStatusChip[]
}

/**
 * Four independent setup facts, rendered side by side.
 *
 * Deliberately NOT a numbered progress tracker with connecting bars. Nothing in the backend
 * orders these: `requestUnderwriting` checks only that a business row exists, at both the
 * controller and the service layer, so an admin can request underwriting the moment the profile
 * saves. Numbered steps would tell them otherwise. Do not add step numbers, arrows or
 * connectors here — see the design doc §4.1.
 *
 * "Documents" is absent because nothing persists supplemental uploads (no entity, repository or
 * read endpoint in para-node), so any state shown for it would be invented.
 */
const BusinessSetupStatusStripComponent = ({ chips }: Props) => {
  return (
    <div
      data-testid="admin-customer-business-setup-strip"
      css={tw`flex flex-row flex-wrap items-stretch gap-3`}
    >
      {chips.map((chip) => {
        return (
          <div
            key={chip.key}
            data-testid={`business-setup-chip-${chip.key}`}
            css={tw`min-w-[160px] flex-1 rounded-lg border border-solid border-shades-100 bg-shades-0 px-4 py-3`}
          >
            <Typography variant="subtitle2" css={tw`mb-1 block text-shades-400`}>
              {chip.label}
            </Typography>
            <Typography
              variant="h6"
              css={chip.isComplete ? tw`text-success-500` : tw`text-shades-500`}
            >
              {chip.value}
            </Typography>
          </div>
        )
      })}
    </div>
  )
}

export const BusinessSetupStatusStrip = memo(BusinessSetupStatusStripComponent)
